import React, { useCallback, useEffect, useMemo, useState } from "react"
import axios from "axios"
import { useSearchParams } from "react-router-dom"
import { useActiveClinic } from "../../context/ClinicContext"
import { useClinicPlanSnapshot } from "../../features/subscriptions/hooks/useClinicPlanSnapshot"
import { useClinicSubscription } from "../../features/subscriptions/hooks/useClinicSubscription"
import SettingsModuleScaffold from "./SettingsModuleScaffold"
import { DEFAULT_USER_ID } from "../../config/appConfig"
import {
  resolveClinicLabel,
  formatMoney,
  humanSpecialtyLabel,
  SectionCard,
  FieldBlock,
  MetricCard,
  InsightCard,
  TopStateCard,
  EmptyState,
  EditableListItem,
  EditModalShell,
  inputStyle,
  listStyle,
  permissionsGridStyle,
  checkboxCardStyle,
  checkboxTitleStyle,
  checkboxMetaStyle,
  actionButtonStyle,
  primaryButtonStyle,
  secondaryButtonStyle
} from "./settingsShared"

const tabs = [
  { key: "catalog", label: "Catálogo" },
  { key: "permissions", label: "Permisos" }
]

const specialtyOptions = [
  { value: "", label: "Sin especialidad específica" },
  { value: "GENERAL", label: "General" },
  { value: "ORTODONCIA", label: "Ortodoncia" },
  { value: "ENDODONCIA", label: "Endodoncia" },
  { value: "CIRUGIA", label: "Cirugía" },
  { value: "IMPLANTOLOGIA", label: "Implantología" },
  { value: "PROSTODONCIA", label: "Prostodoncia" },
  { value: "PERIODONCIA", label: "Periodoncia" },
  { value: "PEDIATRIA", label: "Odontopediatría" }
]

export default function TreatmentsPermissionsPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const { activeClinicId } = useActiveClinic()

  const rawTab = searchParams.get("tab")
  const activeTab = rawTab === "permissions" ? "permissions" : "catalog"

  const clinicId =
    activeClinicId && activeClinicId !== "ALL" ? activeClinicId : null

  const userId =
    localStorage.getItem("odontoflow_user_id") || DEFAULT_USER_ID

  const [clinicRows, setClinicRows] = useState([])
  const [treatments, setTreatments] = useState([])
  const [doctors, setDoctors] = useState([])

  const [loadingTreatments, setLoadingTreatments] = useState(false)
  const [loadingDoctors, setLoadingDoctors] = useState(false)
  const [loadingPermissions, setLoadingPermissions] = useState(false)

  const [treatmentName, setTreatmentName] = useState("")
  const [treatmentDuration, setTreatmentDuration] = useState("30")
  const [treatmentPrice, setTreatmentPrice] = useState("0")
  const [treatmentSpecialty, setTreatmentSpecialty] = useState("")
  const [savingTreatment, setSavingTreatment] = useState(false)

  const [selectedDoctorId, setSelectedDoctorId] = useState("")
  const [doctorSpecialty, setDoctorSpecialty] = useState("")
  const [selectedTreatmentIds, setSelectedTreatmentIds] = useState([])
  const [savingPermissions, setSavingPermissions] = useState(false)

  const [editingTreatment, setEditingTreatment] = useState(null)
  const [editTreatmentName, setEditTreatmentName] = useState("")
  const [editTreatmentDuration, setEditTreatmentDuration] = useState("30")
  const [editTreatmentPrice, setEditTreatmentPrice] = useState("0")
  const [editTreatmentSpecialty, setEditTreatmentSpecialty] = useState("")
  const [savingTreatmentEdit, setSavingTreatmentEdit] = useState(false)

  const [error, setError] = useState("")
  const [success, setSuccess] = useState("")

  const {
    data: planSnapshot,
    refresh: refreshPlanSnapshot
  } = useClinicPlanSnapshot(clinicId)

  const { subscription } = useClinicSubscription(clinicId)

  const currentPlanName = planSnapshot?.plan?.name || "Sin plan activo"
  const accountName = planSnapshot?.accountName || "tu cuenta"

  const isReadOnly = Boolean(subscription?.access?.isReadOnly)
  const readOnlyReason =
    subscription?.access?.reason || "La clínica está en modo solo lectura."

  const treatmentsCount =
    treatments.length || Number(planSnapshot?.usage?.treatmentsCount || 0)

  const doctorsCount =
    doctors.length || Number(planSnapshot?.usage?.doctorsCount || 0)

  const clinicsCount = clinicRows.length
  const activeClinicLabel = useMemo(
    () => resolveClinicLabel(clinicRows, clinicId),
    [clinicRows, clinicId]
  )
  const showActiveClinicMetric = clinicsCount > 1 && !!activeClinicLabel

  const selectedDoctor = useMemo(
    () => doctors.find((doctor) => doctor.id === selectedDoctorId) || null,
    [doctors, selectedDoctorId]
  )

  const handleTabChange = (tab) => {
    setSearchParams({ tab })
    setError("")
    setSuccess("")
  }

  const loadClinics = useCallback(async () => {
    try {
      const { data } = await axios.get("/api/multi-clinic/user-clinics", {
        params: { userId }
      })
      setClinicRows(Array.isArray(data) ? data : [])
    } catch {
      setClinicRows([])
    }
  }, [userId])

  const loadTreatments = useCallback(async () => {
    if (!clinicId) {
      setTreatments([])
      return
    }

    try {
      setLoadingTreatments(true)
      const { data } = await axios.get("/api/treatments", {
        params: { clinicId, limit: 300 }
      })
      setTreatments(Array.isArray(data) ? data : [])
    } catch (err) {
      const message =
        err?.response?.data?.message || "No se pudieron cargar los tratamientos"
      setError(Array.isArray(message) ? message[0] : message)
    } finally {
      setLoadingTreatments(false)
    }
  }, [clinicId])

  const loadDoctors = useCallback(async () => {
    if (!clinicId) {
      setDoctors([])
      return
    }

    try {
      setLoadingDoctors(true)
      const { data } = await axios.get("/api/multi-clinic/clinic-doctors", {
        params: { clinicId }
      })
      const rows = Array.isArray(data) ? data : []
      setDoctors(rows)

      if (rows.length > 0 && !selectedDoctorId) {
        setSelectedDoctorId(rows[0].id)
        setDoctorSpecialty(rows[0].specialty || "")
      }
    } catch (err) {
      const message =
        err?.response?.data?.message || "No se pudieron cargar los doctores"
      setError(Array.isArray(message) ? message[0] : message)
    } finally {
      setLoadingDoctors(false)
    }
  }, [clinicId, selectedDoctorId])

  const loadPermissions = useCallback(
    async (doctorIdParam) => {
      const doctorId = doctorIdParam || selectedDoctorId
      if (!doctorId) {
        setSelectedTreatmentIds([])
        return
      }

      try {
        setLoadingPermissions(true)
        const { data } = await axios.get("/api/doctor-treatments/permissions", {
          params: { doctorId }
        })
        setSelectedTreatmentIds(
          Array.isArray(data) ? data.map((item) => item.treatmentId) : []
        )
      } catch {
        setSelectedTreatmentIds([])
      } finally {
        setLoadingPermissions(false)
      }
    },
    [selectedDoctorId]
  )

  useEffect(() => {
    loadClinics()
    loadTreatments()
    loadDoctors()
  }, [loadClinics, loadTreatments, loadDoctors])

  useEffect(() => {
    if (!selectedDoctor) return
    setDoctorSpecialty(selectedDoctor.specialty || "")
    loadPermissions(selectedDoctor.id)
  }, [selectedDoctor, loadPermissions])

  async function handleCreateTreatment() {
    try {
      setSavingTreatment(true)
      setError("")
      setSuccess("")

      await axios.post("/api/treatments", {
        clinicId,
        name: treatmentName.trim(),
        durationMinutes: Number(treatmentDuration || 0),
        price: Number(treatmentPrice || 0),
        requiredSpecialty: treatmentSpecialty || null
      })

      setTreatmentName("")
      setTreatmentDuration("30")
      setTreatmentPrice("0")
      setTreatmentSpecialty("")

      await loadTreatments()
      await refreshPlanSnapshot?.()
      window.dispatchEvent(new Event("odontoflow:subscription-updated"))

      setSuccess("Tratamiento creado correctamente.")
    } catch (err) {
      const message =
        err?.response?.data?.message || "No se pudo crear el tratamiento"
      setError(Array.isArray(message) ? message[0] : message)
    } finally {
      setSavingTreatment(false)
    }
  }

  function openEditTreatmentModal(treatment) {
    setEditingTreatment(treatment)
    setEditTreatmentName(treatment?.name || "")
    setEditTreatmentDuration(String(Number(treatment?.durationMinutes || 30)))
    setEditTreatmentPrice(String(Number(treatment?.price || 0)))
    setEditTreatmentSpecialty(treatment?.requiredSpecialty || "")
    setError("")
    setSuccess("")
  }

  function closeEditTreatmentModal() {
    if (savingTreatmentEdit) return
    setEditingTreatment(null)
    setEditTreatmentName("")
    setEditTreatmentDuration("30")
    setEditTreatmentPrice("0")
    setEditTreatmentSpecialty("")
  }

  async function updateTreatmentRequest(treatmentId, payload) {
    return axios.patch("/api/treatments", {
      id: treatmentId,
      ...payload
    })
  }

  async function handleSaveTreatmentEdit() {
    try {
      if (!editingTreatment?.id) return

      setSavingTreatmentEdit(true)
      setError("")
      setSuccess("")

      await updateTreatmentRequest(editingTreatment.id, {
        clinicId,
        name: editTreatmentName.trim(),
        durationMinutes: Number(editTreatmentDuration || 0),
        price: Number(editTreatmentPrice || 0),
        requiredSpecialty: editTreatmentSpecialty || null
      })

      await loadTreatments()
      await refreshPlanSnapshot?.()
      window.dispatchEvent(new Event("odontoflow:subscription-updated"))

      closeEditTreatmentModal()
      setSuccess("Tratamiento actualizado correctamente.")
    } catch (err) {
      const message =
        err?.response?.data?.message || "No se pudo actualizar el tratamiento"
      setError(Array.isArray(message) ? message[0] : message)
    } finally {
      setSavingTreatmentEdit(false)
    }
  }

  async function handleSavePermissions() {
    try {
      if (!selectedDoctorId) return

      setSavingPermissions(true)
      setError("")
      setSuccess("")

      await axios.patch("/api/multi-clinic/doctor-specialty", {
        doctorId: selectedDoctorId,
        specialty: doctorSpecialty || null
      })

      await axios.post("/api/doctor-treatments/permissions/replace", {
        doctorId: selectedDoctorId,
        clinicId,
        treatmentIds: selectedTreatmentIds
      })

      await loadDoctors()
      await loadPermissions(selectedDoctorId)

      setSuccess("Permisos y especialidad guardados correctamente.")
    } catch (err) {
      const message =
        err?.response?.data?.message || "No se pudo guardar la configuración"
      setError(Array.isArray(message) ? message[0] : message)
    } finally {
      setSavingPermissions(false)
    }
  }

  const toggleTreatment = (treatmentId) => {
    setSelectedTreatmentIds((prev) =>
      prev.includes(treatmentId)
        ? prev.filter((id) => id !== treatmentId)
        : [...prev, treatmentId]
    )
  }

  const canCreateTreatment =
    !!clinicId &&
    !savingTreatment &&
    !isReadOnly &&
    !!treatmentName.trim() &&
    Number(treatmentDuration || 0) > 0 &&
    Number(treatmentPrice || 0) >= 0

  const canSavePermissions =
    !!clinicId &&
    !!selectedDoctorId &&
    !savingPermissions &&
    !isReadOnly

  const canSaveTreatmentEdit =
    !!editingTreatment?.id &&
    !savingTreatmentEdit &&
    !!editTreatmentName.trim() &&
    Number(editTreatmentDuration || 0) > 0 &&
    Number(editTreatmentPrice || 0) >= 0

  const catalogStatus = useMemo(() => {
    if (!clinicId) {
      return {
        label: "Selecciona clínica",
        tone: "warning",
        detail: "Debes seleccionar una clínica específica para administrar el catálogo."
      }
    }

    if (treatmentsCount === 0) {
      return {
        label: "Pendiente",
        tone: "warning",
        detail: "La clínica activa todavía no tiene tratamientos cargados."
      }
    }

    return {
      label: "Operativo",
      tone: "success",
      detail: "El catálogo ya existe y puede seguir afinándose con precios, duración y estructura."
    }
  }, [clinicId, treatmentsCount])

  const permissionsStatus = useMemo(() => {
    if (!clinicId) {
      return {
        label: "Selecciona clínica",
        tone: "warning",
        detail: "Debes seleccionar una clínica específica para administrar permisos clínicos."
      }
    }

    if (doctorsCount === 0) {
      return {
        label: "Pendiente",
        tone: "warning",
        detail: "Primero necesitas doctores registrados para poder asignar permisos."
      }
    }

    if (treatmentsCount === 0) {
      return {
        label: "Incompleto",
        tone: "warning",
        detail: "Ya hay doctores, pero faltan tratamientos para configurar permisos."
      }
    }

    return {
      label: "Configurable",
      tone: "info",
      detail: "La clínica ya tiene base suficiente para definir qué tratamientos realiza cada doctor."
    }
  }, [clinicId, doctorsCount, treatmentsCount])

  const actions = (
    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
      <button
        onClick={() => handleTabChange("catalog")}
        style={{
          ...actionButtonStyle,
          background: activeTab === "catalog" ? "#ffffff" : "rgba(255,255,255,0.08)",
          color: activeTab === "catalog" ? "#0f172a" : "#ffffff",
          border:
            activeTab === "catalog"
              ? "1px solid rgba(255,255,255,0.40)"
              : "1px solid rgba(255,255,255,0.16)"
        }}
      >
        Ver catálogo
      </button>

      <button
        onClick={() => handleTabChange("permissions")}
        style={{
          ...actionButtonStyle,
          background:
            activeTab === "permissions" ? "#ffffff" : "rgba(255,255,255,0.08)",
          color: activeTab === "permissions" ? "#0f172a" : "#ffffff",
          border:
            activeTab === "permissions"
              ? "1px solid rgba(255,255,255,0.40)"
              : "1px solid rgba(255,255,255,0.16)"
        }}
      >
        Ver permisos
      </button>
    </div>
  )

  return (
    <>
      <SettingsModuleScaffold
        eyebrow="Motor del sistema"
        title="Tratamientos y permisos"
        subtitle="Agrupa catálogo, duración, precios base y control operativo por tratamiento para que esta lógica deje de vivir separada."
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={handleTabChange}
        actions={actions}
      >
        {!clinicId ? (
          <TopStateCard tone="warning" title="Clínica no seleccionada">
            Selecciona una clínica específica para administrar tratamientos y permisos.
          </TopStateCard>
        ) : null}

        {isReadOnly ? (
          <TopStateCard tone="danger" title="Modo solo lectura">
            {readOnlyReason}
          </TopStateCard>
        ) : null}

        {error ? (
          <TopStateCard tone="danger" title="Error">
            {error}
          </TopStateCard>
        ) : null}

        {success ? (
          <TopStateCard tone="success" title="Listo">
            {success}
          </TopStateCard>
        ) : null}

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))",
            gap: 12
          }}
        >
          <MetricCard
            label="Tratamientos"
            value={loadingTreatments ? "..." : String(treatmentsCount)}
            helper="En clínica activa"
            tone={catalogStatus.tone}
          />

          <MetricCard
            label="Doctores"
            value={loadingDoctors ? "..." : String(doctorsCount)}
            helper="Base para permisos"
            tone={permissionsStatus.tone}
          />

          <MetricCard
            label="Plan"
            value={currentPlanName}
            helper={accountName}
            tone="info"
          />

          {showActiveClinicMetric ? (
            <MetricCard
              label="Clínica activa"
              value={activeClinicLabel}
              helper="Contexto actual"
              tone="neutral"
            />
          ) : null}
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
            gap: 12
          }}
        >
          <InsightCard
            title="Catálogo clínico"
            status={catalogStatus.label}
            tone={catalogStatus.tone}
            detail={catalogStatus.detail}
            ctaLabel="Ir a catálogo"
            onClick={() => handleTabChange("catalog")}
          />

          <InsightCard
            title="Permisos por doctor"
            status={permissionsStatus.label}
            tone={permissionsStatus.tone}
            detail={permissionsStatus.detail}
            ctaLabel="Ir a permisos"
            onClick={() => handleTabChange("permissions")}
          />
        </div>

        {activeTab === "catalog" ? (
          <div style={{ display: "grid", gap: 14 }}>
            <SectionCard
              title="Alta de tratamiento"
              subtitle="Crea tratamientos base con duración, precio y especialidad requerida."
            >
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                  gap: 12
                }}
              >
                <FieldBlock label="Nombre">
                  <input
                    value={treatmentName}
                    onChange={(e) => setTreatmentName(e.target.value)}
                    placeholder="Nombre del tratamiento"
                    style={inputStyle}
                  />
                </FieldBlock>

                <FieldBlock label="Duración (min)">
                  <input
                    type="number"
                    value={treatmentDuration}
                    onChange={(e) => setTreatmentDuration(e.target.value)}
                    placeholder="30"
                    style={inputStyle}
                  />
                </FieldBlock>

                <FieldBlock label="Precio">
                  <input
                    type="number"
                    value={treatmentPrice}
                    onChange={(e) => setTreatmentPrice(e.target.value)}
                    placeholder="0"
                    style={inputStyle}
                  />
                </FieldBlock>

                <FieldBlock label="Especialidad requerida">
                  <select
                    value={treatmentSpecialty}
                    onChange={(e) => setTreatmentSpecialty(e.target.value)}
                    style={inputStyle}
                  >
                    {specialtyOptions.map((option) => (
                      <option key={option.value || "none"} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </FieldBlock>
              </div>

              <div style={{ marginTop: 12, display: "flex", gap: 10, flexWrap: "wrap" }}>
                <button
                  onClick={handleCreateTreatment}
                  disabled={!canCreateTreatment}
                  style={{
                    ...primaryButtonStyle,
                    opacity: canCreateTreatment ? 1 : 0.6,
                    cursor: canCreateTreatment ? "pointer" : "not-allowed"
                  }}
                >
                  {savingTreatment ? "Guardando..." : "Crear tratamiento"}
                </button>
              </div>
            </SectionCard>

            <SectionCard
              title="Listado de tratamientos"
              subtitle="Tratamientos registrados en la clínica activa."
            >
              {loadingTreatments ? (
                <EmptyState text="Cargando tratamientos..." />
              ) : treatments.length === 0 ? (
                <EmptyState text="No hay tratamientos registrados todavía." />
              ) : (
                <div style={listStyle}>
                  {treatments.map((treatment) => (
                    <EditableListItem
                      key={treatment.id}
                      title={treatment.name || "Tratamiento"}
                      metaLeft={`${Number(treatment.durationMinutes || 0)} min · ${formatMoney(
                        treatment.price || 0
                      )}`}
                      metaRight={humanSpecialtyLabel(treatment.requiredSpecialty)}
                      onEdit={() => openEditTreatmentModal(treatment)}
                    />
                  ))}
                </div>
              )}
            </SectionCard>
          </div>
        ) : null}

        {activeTab === "permissions" ? (
          <div style={{ display: "grid", gap: 14 }}>
            <SectionCard
              title="Configuración por doctor"
              subtitle="Selecciona doctor, define especialidad principal y marca qué tratamientos realiza."
            >
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                  gap: 12
                }}
              >
                <FieldBlock label="Doctor">
                  <select
                    value={selectedDoctorId}
                    onChange={(e) => setSelectedDoctorId(e.target.value)}
                    style={inputStyle}
                    disabled={loadingDoctors || doctors.length === 0}
                  >
                    {doctors.length === 0 ? (
                      <option value="">No hay doctores registrados</option>
                    ) : (
                      doctors.map((doctor) => (
                        <option key={doctor.id} value={doctor.id}>
                          {doctor.name || "Doctor"}
                        </option>
                      ))
                    )}
                  </select>
                </FieldBlock>

                <FieldBlock label="Especialidad principal">
                  <select
                    value={doctorSpecialty}
                    onChange={(e) => setDoctorSpecialty(e.target.value)}
                    style={inputStyle}
                    disabled={!selectedDoctorId}
                  >
                    {specialtyOptions.map((option) => (
                      <option key={option.value || "none"} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </FieldBlock>
              </div>

              <div style={{ marginTop: 12 }}>
                {loadingPermissions ? (
                  <EmptyState text="Cargando permisos del doctor..." />
                ) : treatments.length === 0 ? (
                  <EmptyState text="No hay tratamientos para asignar todavía." />
                ) : (
                  <div style={permissionsGridStyle}>
                    {treatments.map((treatment) => {
                      const checked = selectedTreatmentIds.includes(treatment.id)

                      return (
                        <label key={treatment.id} style={checkboxCardStyle}>
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleTreatment(treatment.id)}
                            disabled={!selectedDoctorId}
                          />
                          <div style={{ minWidth: 0 }}>
                            <div style={checkboxTitleStyle}>
                              {treatment.name || "Tratamiento"}
                            </div>
                            <div style={checkboxMetaStyle}>
                              {Number(treatment.durationMinutes || 0)} min ·{" "}
                              {formatMoney(treatment.price || 0)}
                            </div>
                          </div>
                        </label>
                      )
                    })}
                  </div>
                )}
              </div>

              <div style={{ marginTop: 12, display: "flex", gap: 10, flexWrap: "wrap" }}>
                <button
                  onClick={handleSavePermissions}
                  disabled={!canSavePermissions}
                  style={{
                    ...primaryButtonStyle,
                    opacity: canSavePermissions ? 1 : 0.6,
                    cursor: canSavePermissions ? "pointer" : "not-allowed"
                  }}
                >
                  {savingPermissions ? "Guardando..." : "Guardar configuración"}
                </button>
              </div>
            </SectionCard>
          </div>
        ) : null}
      </SettingsModuleScaffold>

      {editingTreatment ? (
        <EditModalShell
          title="Editar tratamiento"
          subtitle="Ajusta nombre, duración, precio y especialidad requerida."
          onClose={closeEditTreatmentModal}
          actions={
            <>
              <button
                onClick={closeEditTreatmentModal}
                style={secondaryButtonStyle}
                disabled={savingTreatmentEdit}
              >
                Cancelar
              </button>

              <button
                onClick={handleSaveTreatmentEdit}
                disabled={!canSaveTreatmentEdit}
                style={{
                  ...primaryButtonStyle,
                  opacity: canSaveTreatmentEdit ? 1 : 0.6,
                  cursor: canSaveTreatmentEdit ? "pointer" : "not-allowed"
                }}
              >
                {savingTreatmentEdit ? "Guardando..." : "Guardar cambios"}
              </button>
            </>
          }
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
              gap: 12
            }}
          >
            <FieldBlock label="Nombre">
              <input
                value={editTreatmentName}
                onChange={(e) => setEditTreatmentName(e.target.value)}
                placeholder="Nombre del tratamiento"
                style={inputStyle}
              />
            </FieldBlock>

            <FieldBlock label="Duración (min)">
              <input
                type="number"
                value={editTreatmentDuration}
                onChange={(e) => setEditTreatmentDuration(e.target.value)}
                placeholder="30"
                style={inputStyle}
              />
            </FieldBlock>

            <FieldBlock label="Precio">
              <input
                type="number"
                value={editTreatmentPrice}
                onChange={(e) => setEditTreatmentPrice(e.target.value)}
                placeholder="0"
                style={inputStyle}
              />
            </FieldBlock>

            <FieldBlock label="Especialidad requerida">
              <select
                value={editTreatmentSpecialty}
                onChange={(e) => setEditTreatmentSpecialty(e.target.value)}
                style={inputStyle}
              >
                {specialtyOptions.map((option) => (
                  <option key={option.value || "none"} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </FieldBlock>
          </div>
        </EditModalShell>
      ) : null}
    </>
  )
}